import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Sparkles, ArrowLeft, Users, Play, Check, User, Star, Crown
} from 'lucide-react';

const FamilyMode = () => {
  const { api, user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(user?.active_child_id);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await api.get('/children');
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchChild = async (childId) => {
    setSwitching(childId);
    try {
      await api.post(`/family/switch-child/${childId}`);
      setActiveChildId(childId);
      updateUser({ active_child_id: childId });
      toast.success('Switched profile!');
    } catch (error) {
      toast.error('Failed to switch profile');
    } finally {
      setSwitching(null);
    }
  };

  const startLearning = (childId) => {
    handleSwitchChild(childId);
    navigate(`/learn/${childId}`);
  };

  const getAvatarColor = (index) => {
    const colors = [
      'from-sky-400 to-blue-500',
      'from-pink-400 to-rose-500',
      'from-green-400 to-emerald-500',
      'from-purple-400 to-violet-500',
      'from-orange-400 to-amber-500',
      'from-cyan-400 to-teal-500',
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-sky-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-sky-500" />
              <h1 className="font-fredoka font-bold text-xl text-slate-800">Family Mode</h1>
            </div>
            <div className="w-10" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-10">
          <h2 className="font-fredoka text-3xl font-bold text-slate-800 mb-2">
            Who's Learning Today?
          </h2>
          <p className="text-slate-600">Tap your picture to start!</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-8 text-center">
                  <div className="w-24 h-24 bg-slate-200 rounded-full mx-auto mb-4" />
                  <div className="h-6 w-20 bg-slate-200 rounded mx-auto" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : children.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="w-20 h-20 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-sky-500" />
              </div>
              <h3 className="font-bold text-xl text-slate-800 mb-2">No children yet</h3>
              <p className="text-slate-600 mb-6">Add children to use Family Mode!</p>
              <Link to="/child/new">
                <Button className="bg-sky-500 hover:bg-sky-600">
                  Add First Child
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {children.map((child, index) => (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className={`cursor-pointer transition-all hover:shadow-xl ${
                    activeChildId === child.id ? 'ring-4 ring-sky-400 ring-offset-2' : ''
                  }`}
                  onClick={() => startLearning(child.id)}
                  data-testid={`family-child-${child.id}`}
                >
                  <CardContent className="p-6 text-center relative">
                    {activeChildId === child.id && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-sky-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    
                    <div className={`w-24 h-24 bg-gradient-to-br ${getAvatarColor(index)} rounded-full flex items-center justify-center mx-auto mb-4 text-white text-4xl font-bold shadow-lg`}>
                      {child.name.charAt(0).toUpperCase()}
                    </div>
                    
                    <h3 className="font-fredoka text-xl font-bold text-slate-800 mb-1">
                      {child.name}
                    </h3>
                    
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-3">
                      <Crown className="w-4 h-4 text-yellow-500" />
                      Level {child.current_level}
                      <Star className="w-4 h-4 text-sky-500" />
                      {child.total_xp} XP
                    </div>
                    
                    <Button
                      className="w-full bg-sky-500 hover:bg-sky-600"
                      disabled={switching === child.id}
                      data-testid={`start-${child.id}`}
                    >
                      {switching === child.id ? (
                        'Switching...'
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" /> Play
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
            
            {/* Add Child Card */}
            <Link to="/child/new">
              <Card className="h-full cursor-pointer border-2 border-dashed border-slate-300 hover:border-sky-400 transition-colors">
                <CardContent className="p-6 text-center h-full flex flex-col items-center justify-center">
                  <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl text-slate-400">+</span>
                  </div>
                  <h3 className="font-bold text-lg text-slate-600">Add Child</h3>
                </CardContent>
              </Card>
            </Link>
          </div>
        )}

        {/* Quick Switch Info */}
        <div className="mt-10 text-center">
          <p className="text-sm text-slate-500">
            Family Mode lets multiple children share one device. Just tap to switch!
          </p>
        </div>
      </main>
    </div>
  );
};

export default FamilyMode;
